import type { CriticalApp, CustomerResponse, DashboardApiResponse } from '../types';

/** Map dashboard API projects array to TopProjectsResponse shape */
function mapProjectsToTop10(
  projects: DashboardApiResponse['projects'] = [],
  industryType: string
): CustomerResponse['top_10_projects'] {
  const raw = Array.isArray(projects) ? projects : [];
  const top_projects = raw.map((p: any) => ({
    project_id: p.projectId ?? p.project_id ?? '',
    name: p.projectName ?? p.name ?? '',
    overview_and_goals: p.overview ?? p.overview_and_goals ?? '',
    presidio_controlled: p.presidioControlled ?? p.presidio_controlled,
    modernization_plan_project: p.modernizationPlanProject ?? p.modernization_plan_project,
    start_month: p.startDate ?? p.start_month,
    end_month: p.endDate ?? p.end_month,
    dependency_project_id: p.dependsOnProjectId ?? p.dependency_project_id,
    goals: p.goals ?? [],
    presidio_areas: p.presidioAreas ?? p.presidio_areas ?? [],
  }));
  return { top_projects, industry_type: industryType };
}

/** Transform raw technology_groups + strategic_vendors to TechnologyResponse (key_technology, strategic_vendors) */
function mapTechnologiesToResponse(raw: DashboardApiResponse['technologies']): CustomerResponse['technology'] {
  if (!raw) {
    return { key_technology: [], strategic_vendors: [] };
  }
  const technology_groups = (raw.technology_groups || []).map(
    (g: { group?: string; technology_group_id?: string; technologies?: Array<{ technology_id?: string; name?: string; status?: string; notes?: string }> }) => ({
      group: g.group ?? '',
      technology_group_id: g.technology_group_id ?? '',
      technologies: (g.technologies || []).map(
        (t: { technology_id?: string; name?: string; status?: string; notes?: string }) => ({
          technology_id: t.technology_id ?? '',
          name: t.name,
          status: t.status,
          notes: t.notes,
        })
      ),
    })
  );
  const key_technology = technology_groups.map(
    (g: { group: string; technologies: Array<{ name?: string; status?: string; notes?: string }> }) => ({
      name: g.group,
      divisions: (g.technologies || []).map(
        (t: { name?: string; status?: string; notes?: string }) => ({
          tech: t.name ?? '',
          metric: t.status ?? '',
          description: t.notes ?? '',
        })
      ),
    })
  );
  const strategic_vendors = (raw.strategic_vendors || []).map(
    (v: { vendor?: string; status?: string; technology_id?: string }) => ({
      vendor: v.vendor ?? '',
      status: v.status ?? '',
      ...(v.technology_id != null && { technology_id: v.technology_id }),
    })
  );
  return { key_technology, strategic_vendors, technology_groups };
}

/**
 * Maps the dashboard API response to the shape expected by CustomerResponse (partial).
 */
export function mapDashboardResponseToCustomerResponse(
  dashboard: DashboardApiResponse
): Partial<CustomerResponse> {
  const industryType = dashboard.customer_details?.industry_type ?? '';

  const result: Partial<CustomerResponse> = {
    customer_id: dashboard.customer_id,
    customer_details: dashboard.customer_details,
    goals: dashboard.goals,
    top_10_projects: mapProjectsToTop10(dashboard.projects, industryType),
    // Only merge frameworks when dashboard API returns them; otherwise preserve existing context (e.g. from edit view)
    ...(dashboard.framework != null && Array.isArray(dashboard.framework)
      ? { frameworks: { frameworks: dashboard.framework } }
      : {}),
    ...(dashboard.applications != null
      ? {
          critical_apps: {
            critical_apps_list: dashboard.applications.map((app: CriticalApp & { application_id?: string }) => ({
              ...app,
              app_id: app.app_id ?? app.application_id,
            })),
            industry_type: industryType,
          },
        }
      : {}),
    ...(dashboard.aop != null
      ? {
          art_of_possible: {
            aops: dashboard.aop.map((item) => ({
              area: item.label,
              notes: (item.plans || []).map((plan) => ({
                name: plan.name ?? '',
                rank: plan.rank ?? 0,
                growth: plan.technical_impact ?? '',
                priority: plan.business_impact ?? '',
              })),
            })),
          },
        }
      : {}),
    ...(dashboard.technologies != null
      ? { technology: mapTechnologiesToResponse(dashboard.technologies) }
      : {}),
    ...(dashboard.maturity_transformations != null
      ? { maturity_transformations_by_plan: dashboard.maturity_transformations }
      : {}),
  };

  return result;
}
